/**
 * Stale Renewals Report (C.3 Commit 5)
 *
 * Three-bucket operator view of renewals that have stalled, surfacing
 * the lifecycle hand-offs that need follow-up. Reads from
 * /api/billing/reports/stale-renewals.
 *
 * Buckets:
 *   - drafts_aged: renewal proposal initiated > 30 days ago, still Draft.
 *   - sent_aged:   renewal proposal Sent > 30 days ago, not yet converted.
 *   - overdue:     source AMC end_date passed > 14 days ago, no renewal.
 *
 * Exports CSV per bucket for follow-up by Sales/Accounts teams.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    WarningOctagon,
    Clock,
    PaperPlaneTilt,
    FileText,
    DownloadSimple,
    ArrowUpRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BUCKET_DEFS = [
    {
        key: "overdue",
        label: "Overdue · no renewal initiated",
        icon: WarningOctagon,
        tone: "rose",
        description: "Source AMC's end_date passed more than 14 days ago and no renewal proposal has been initiated yet. Highest-attention items — the commercial conversation hasn't started.",
    },
    {
        key: "sent_aged",
        label: "Sent · not converted",
        icon: PaperPlaneTilt,
        tone: "amber",
        description: "Renewal proposal was Sent more than 30 days ago but hasn't yet been converted to a PO. Likely awaiting client decision — Sales should follow up.",
    },
    {
        key: "drafts_aged",
        label: "Draft · aged",
        icon: FileText,
        tone: "slate",
        description: "Renewal proposal initiated more than 30 days ago but is still in Draft (not Sent). The handover from Initiated to Sent hasn't closed.",
    },
];

const TONE_CLASSES = {
    rose: { border: "border-rose-200", bg: "bg-rose-50/60", icon: "text-rose-600", count: "text-rose-700" },
    amber: { border: "border-amber-200", bg: "bg-amber-50/60", icon: "text-amber-600", count: "text-amber-700" },
    slate: { border: "border-slate-200", bg: "bg-slate-50/60", icon: "text-slate-600", count: "text-slate-800" },
};

const toCsv = (rows, columns) => {
    const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map((c) => c.label).join(",");
    const body = rows
        .map((r) => columns.map((c) => escape(c.value(r))).join(","))
        .join("\n");
    return `${header}\n${body}`;
};

const downloadCsv = (filename, content) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const CSV_COLUMNS = {
    overdue: [
        { label: "AMC ID", value: (r) => r.amc_schedule_id },
        { label: "AMC Name", value: (r) => r.amc_name },
        { label: "Client", value: (r) => r.client?.name },
        { label: "End Date", value: (r) => (r.end_date || "").slice(0, 10) },
        { label: "Days Overdue", value: (r) => r.days_overdue },
    ],
    sent_aged: [
        { label: "AMC ID", value: (r) => r.amc_schedule_id },
        { label: "AMC Name", value: (r) => r.amc_name },
        { label: "Client", value: (r) => r.client?.name },
        { label: "Renewal Proposal", value: (r) => r.renewal_proposal_number },
        { label: "Days Since Initiated", value: (r) => r.days_since_initiated },
    ],
    drafts_aged: [
        { label: "AMC ID", value: (r) => r.amc_schedule_id },
        { label: "AMC Name", value: (r) => r.amc_name },
        { label: "Client", value: (r) => r.client?.name },
        { label: "Renewal Proposal", value: (r) => r.renewal_proposal_number },
        { label: "Days Since Initiated", value: (r) => r.days_since_initiated },
    ],
};

function BucketTable({ bucket, rows }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="px-5 py-6 text-center text-sm text-slate-500" data-testid={`stale-empty-${bucket}`}>
                No items in this bucket.
            </div>
        );
    }
    const isOverdue = bucket === "overdue";
    const isDays = isOverdue ? "Days Overdue" : "Days Since Initiated";
    return (
        <table className="w-full text-sm" data-testid={`stale-table-${bucket}`}>
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                    <th className="px-5 py-3 reedos-eyebrow text-[10px]">AMC</th>
                    <th className="px-5 py-3 reedos-eyebrow text-[10px]">Client</th>
                    <th className="px-5 py-3 reedos-eyebrow text-[10px]">
                        {isOverdue ? "End Date" : "Renewal Proposal"}
                    </th>
                    <th className="px-5 py-3 reedos-eyebrow text-[10px] text-right">{isDays}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr
                        key={r.amc_schedule_id}
                        className="border-t border-slate-100 hover:bg-slate-50/50"
                        data-testid={`stale-row-${r.amc_schedule_id}`}
                    >
                        <td className="px-5 py-3">
                            <Link
                                to={`/amc/${r.amc_schedule_id}`}
                                className="font-mono text-xs font-semibold text-slate-900 hover:underline"
                            >
                                {r.amc_schedule_id}
                            </Link>
                            <div className="text-xs text-slate-500 mt-0.5">{r.amc_name}</div>
                        </td>
                        <td className="px-5 py-3">
                            <Link
                                to={`/clients/${r.client?.id}`}
                                className="text-xs text-slate-900 hover:underline"
                            >
                                {r.client?.name || "—"}
                            </Link>
                        </td>
                        <td className="px-5 py-3">
                            {isOverdue ? (
                                <span className="text-xs text-slate-700">{(r.end_date || "").slice(0, 10)}</span>
                            ) : (
                                <Link
                                    to={`/proposals/${r.renewal_proposal_id}`}
                                    className="font-mono text-xs font-semibold text-indigo-700 hover:underline"
                                >
                                    {r.renewal_proposal_number}
                                </Link>
                            )}
                        </td>
                        <td className="px-5 py-3 text-right">
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200">
                                <Clock size={10} weight="fill" />
                                {isOverdue ? `${r.days_overdue}d overdue` : `${r.days_since_initiated || 0}d ago`}
                            </span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function StaleRenewals() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/billing/reports/stale-renewals");
                setData(r.data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load stale renewals"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const buckets = data?.buckets || {};
    const totals = data?.totals || {};

    return (
        <div data-testid="stale-renewals-page">
            <PageHeader
                eyebrow="Reports · Renewal Lifecycle"
                title="Stale Renewals"
                description="AMC renewals that have stalled at one of the lifecycle hand-offs. Three buckets surface the most actionable items first."
            />
            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Header strip with totals + overall guidance */}
                <div className="reedos-card p-5" data-testid="stale-renewals-totals">
                    <div className="grid grid-cols-3 gap-4">
                        {BUCKET_DEFS.map((b) => {
                            const tone = TONE_CLASSES[b.tone];
                            const Icon = b.icon;
                            const n = totals[b.key] || 0;
                            return (
                                <div
                                    key={b.key}
                                    className={`rounded-lg border p-4 ${tone.border} ${tone.bg}`}
                                    data-testid={`stale-total-${b.key}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Icon size={16} className={tone.icon} />
                                        <span className="reedos-eyebrow text-[10px]">{b.label}</span>
                                    </div>
                                    <div className={`mt-2 reedos-data font-display font-bold text-3xl ${tone.count}`}>
                                        {loading ? "—" : n}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {BUCKET_DEFS.map((b) => {
                    const tone = TONE_CLASSES[b.tone];
                    const Icon = b.icon;
                    const rows = buckets[b.key] || [];
                    return (
                        <div className="reedos-card" key={b.key} data-testid={`stale-bucket-${b.key}`}>
                            <div className={`px-5 py-4 border-b ${tone.border} ${tone.bg} flex items-center justify-between`}>
                                <div className="flex items-center gap-2">
                                    <Icon size={18} className={tone.icon} />
                                    <div>
                                        <div className="reedos-eyebrow">Bucket</div>
                                        <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                            {b.label} <span className="ml-1 text-xs text-slate-500">({rows.length})</span>
                                        </h3>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    disabled={rows.length === 0}
                                    onClick={() => downloadCsv(
                                        `stale-renewals-${b.key}-${new Date().toISOString().slice(0, 10)}.csv`,
                                        toCsv(rows, CSV_COLUMNS[b.key]),
                                    )}
                                    data-testid={`stale-csv-${b.key}`}
                                >
                                    <DownloadSimple size={14} className="mr-1.5" /> CSV
                                </Button>
                            </div>
                            <div className="px-5 py-3 text-xs text-slate-600 border-b border-slate-100">
                                {b.description}
                            </div>
                            <BucketTable bucket={b.key} rows={rows} />
                        </div>
                    );
                })}

                <div className="flex justify-end">
                    <Link
                        to="/amc"
                        className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                    >
                        Back to AMC list <ArrowUpRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
